import { mainBackendApi } from '@/services'
import type { Card, IssueCardRequest, TopupCardRequest, TopupCardResponse } from '@/models/Card'

export interface CardSensitive {
	cardNumber: string
	cvv: string
	expiry: string
}

const normalizeCard = (card: Card): Card => ({
	...card,
	balance: Number(card.balance),
})

export const CardService = mainBackendApi.injectEndpoints({
	endpoints: build => ({
		getCards: build.query<Card[], void>({
			query: () => ({
				url: '/client/card',
				method: 'GET',
			}),
			transformResponse: (res: Card[]) => res.map(normalizeCard),
			providesTags: ['Card'],
		}),
		issueCard: build.mutation<Card, IssueCardRequest>({
			query: (body) => ({
				url: '/client/card',
				method: 'POST',
				body: {
					card_product_id: body.cardProductId,
					...(body.promoCode ? { promo_code: body.promoCode } : {}),
				},
			}),
			transformResponse: (res: Card) => normalizeCard(res),
			invalidatesTags: ['Card', 'Balance', 'Activity'],
		}),
		getCardSensitive: build.query<CardSensitive, string>({
			query: (cardId) => ({
				url: `/client/card/${cardId}/sensitive`,
				method: 'GET',
			}),
			transformResponse: (res: any): CardSensitive => ({
				cardNumber: res.card_no ?? res.cardNo ?? res.card_number ?? res.cardNumber ?? '',
				cvv: res.cvv ?? res.CVV ?? '',
				expiry: res.expiry ?? res.expire ?? res.exp ?? '',
			}),
		}),
		freezeCard: build.mutation<Card, string>({
			query: (cardId) => ({
				url: `/client/card/${cardId}/freeze`,
				method: 'POST',
			}),
			transformResponse: (res: Card) => normalizeCard(res),
			invalidatesTags: ['Card'],
		}),
		unfreezeCard: build.mutation<Card, string>({
			query: (cardId) => ({
				url: `/client/card/${cardId}/unfreeze`,
				method: 'POST',
			}),
			transformResponse: (res: Card) => normalizeCard(res),
			invalidatesTags: ['Card'],
		}),
		topupCard: build.mutation<TopupCardResponse, TopupCardRequest>({
			query: (body) => ({
				url: '/client/card/topup',
				method: 'POST',
				body: {
					card_id: body.cardId,
					amount: body.amount,
					idempotency_key: body.idempotencyKey,
					...(body.promoCode ? { promo_code: body.promoCode } : {}),
				},
			}),
			transformResponse: (res: TopupCardResponse) => ({
				...res,
				amount: Number(res.amount),
				fee: Number(res.fee),
			}),
			invalidatesTags: ['Card', 'Balance', 'Activity'],
		}),
	}),
})
